import { DatePipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  Output,
  signal,
} from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AutofocusDirective } from 'src/app/shared/directives/autofocus.directive';

import { ContentDocumentLink } from 'src/app/models/content-document-link.model';
import {
  closeDropdown,
  closeDropdownOnOutsideClick,
  stopEvent,
  toggleDropdown,
} from 'src/app/shared/utils/dropdown.util';
import {
  InlineErrors,
  shouldShowInlineError,
  updateInlineErrors,
} from 'src/app/shared/utils/form-validation.util';

@Component({
  selector: 'app-ticket-billing',
  templateUrl: './ticket-billing.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    DatePipe,
    ReactiveFormsModule,
    MatIconModule,
    MatProgressSpinnerModule,
    AutofocusDirective,
  ],
})
export class TicketBillingComponent {
  @Input() invoices: ContentDocumentLink[];

  @Input() isLoading: boolean;

  @Input() submissionError: boolean;

  @Input() isSubmitting: boolean;

  @Output() submitBillForm = new EventEmitter();

  @Input() errorMessage: string;

  billTicketGroup = new UntypedFormGroup({
    invoiceDate: new UntypedFormControl('', Validators.required),
    summary: new UntypedFormControl('', Validators.required),
  });

  formSubmitAttempted = signal(false);

  inlineErrors: InlineErrors = {};

  isInvoiceDropdownOpen = signal(false);

  invoiceSearchTerm = signal('');

  private elementRef = inject(ElementRef);

  private datePipe = inject(DatePipe);

  get filteredInvoices(): ContentDocumentLink[] {
    if (!this.invoices?.length) return [];
    if (!this.invoiceSearchTerm()) return this.invoices;
    const term = this.invoiceSearchTerm().toLowerCase();
    return this.invoices.filter((inv) => {
      const formatted =
        this.datePipe.transform(
          inv.ContentDocument.Title.slice(0, 10),
          'longDate',
        ) ?? '';
      return formatted.toLowerCase().includes(term);
    });
  }

  get selectedInvoiceLabel(): string {
    const val = this.billTicketGroup.value.invoiceDate;
    if (!val) return '';
    return this.datePipe.transform(val.slice(0, 10), 'longDate') ?? val;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    closeDropdownOnOutsideClick(
      this.elementRef,
      event,
      this.isInvoiceDropdownOpen,
      this.invoiceSearchTerm,
    );
  }

  toggleInvoiceDropdown(event: Event): void {
    stopEvent(event);
    toggleDropdown(this.isInvoiceDropdownOpen, this.invoiceSearchTerm);
  }

  selectInvoice(title: string, event: Event): void {
    stopEvent(event);
    this.billTicketGroup.controls.invoiceDate.setValue(title);
    closeDropdown(this.isInvoiceDropdownOpen, this.invoiceSearchTerm);
    this.canSubmit();
  }

  onSearchInput(event: Event): void {
    this.invoiceSearchTerm.set((event.target as HTMLInputElement).value);
  }

  private readonly fieldErrors: Record<string, string> = {
    invoiceDate: 'Please select an invoice date',
    summary: 'Please describe your issue',
  };

  canSubmit(): boolean {
    return updateInlineErrors(
      this.billTicketGroup,
      this.inlineErrors,
      this.fieldErrors,
    );
  }

  shouldShowError(fieldName: string): boolean {
    return shouldShowInlineError(
      this.billTicketGroup,
      this.inlineErrors,
      fieldName,
      this.formSubmitAttempted(),
    );
  }

  submitTicket(): void {
    if (this.billTicketGroup.valid) {
      const formattedDate = this.datePipe.transform(
        this.billTicketGroup.value.invoiceDate.slice(0, 10),
        'yyyy-MM-dd',
      );
      const request = {
        invoice_date: formattedDate,
        description: this.billTicketGroup.value.summary as string,
      };
      this.submitBillForm.emit(request);
    } else {
      this.canSubmit();
      this.formSubmitAttempted.set(true);
    }
  }
}
